import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

interface ConfirmOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

interface ConfirmContextValue {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

interface ConfirmProviderProps {
    children: ReactNode;
}

/**
 * 確認對話框 Provider
 * 包裹在 App 最外層，提供全域的 confirm 功能
 */
export function ConfirmProvider({ children }: ConfirmProviderProps) {
    const [dialogState, setDialogState] = useState<{
        open: boolean;
        options: ConfirmOptions;
        resolve: ((value: boolean) => void) | null;
    }>({
        open: false,
        options: { title: '', message: '' },
        resolve: null,
    });

    const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setDialogState({
                open: true,
                options,
                resolve,
            });
        });
    }, []);

    const handleConfirm = useCallback(() => {
        dialogState.resolve?.(true);
        setDialogState((prev) => ({ ...prev, open: false, resolve: null }));
    }, [dialogState.resolve]);

    const handleCancel = useCallback(() => {
        dialogState.resolve?.(false);
        setDialogState((prev) => ({ ...prev, open: false, resolve: null }));
    }, [dialogState.resolve]);

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            <ConfirmDialog
                open={dialogState.open}
                title={dialogState.options.title}
                message={dialogState.options.message}
                confirmText={dialogState.options.confirmText}
                cancelText={dialogState.options.cancelText}
                confirmColor={dialogState.options.confirmColor}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </ConfirmContext.Provider>
    );
}

/**
 * 使用確認對話框的 Hook
 *
 * @example
 * const confirm = useConfirm();
 *
 * const handleDelete = async () => {
 *     const confirmed = await confirm({
 *         title: '確認刪除',
 *         message: '確定要刪除此項目嗎？此操作無法復原。',
 *         confirmText: '刪除',
 *         confirmColor: 'error',
 *     });
 *
 *     if (confirmed) {
 *         // 執行刪除
 *     }
 * };
 */
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context.confirm;
}
